/** PA Working Hours (BOXY-523): day table of all pickup agents + 7-day shift timeline for one. */
import { useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { PageHeader, Panel, Select, tbl } from '../components/ui';
import DayTable from '../components/working-hours/DayTable';
import ShiftTimeline from '../components/working-hours/ShiftTimeline';
import { workingHoursApi } from '../services/workingHours.api';
import { cn } from '../utils/cn';

const FIVE_MIN = 5 * 60_000;
// Same rule as the server: before 11:00 Baghdad (08:00Z) ops are still looking at yesterday.
const currentDay = () => new Date(Date.now() - 8 * 3_600_000).toISOString().slice(0, 10);
const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const Spinner = () => <Loader2 className="mx-auto my-8 h-6 w-6 motion-safe:animate-spin text-gray-400" />;

export default function WorkingHours() {
  const today = currentDay();
  const [date, setDate] = useState(today);
  const [picked, setPicked] = useState<number | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const live = date === today ? FIVE_MIN : false;

  const day = useQuery({
    queryKey: ['working-hours', 'day', date],
    queryFn: () => workingHoursApi.getDay(date),
    refetchInterval: live,
    placeholderData: keepPreviousData,
  });
  const rows = day.data?.rows ?? [];
  // A picked agent who did not work the chosen day falls back to the first row, so name and timeline agree.
  const driverId = picked !== null && rows.some((r) => r.driverId === picked) ? picked : rows[0]?.driverId ?? null;
  const selected = rows.find((r) => r.driverId === driverId);
  // Never fetch the previous agent against the new day.
  const week = useQuery({
    queryKey: ['working-hours', 'driver', driverId, date],
    queryFn: () => workingHoursApi.getDriverWeek(driverId!, date),
    enabled: driverId !== null && !day.isPlaceholderData,
    refetchInterval: live,
    // Keep the old week only for the same agent (a day change); another agent's week must not show under the new name.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === driverId ? prev : undefined),
  });

  // Picking in the table is the only way the timeline moves out of view, so bring it into view.
  const pickFromTable = (id: number) => {
    setPicked(id);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    timelineRef.current?.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Working Hours"
        subtitle="Pickup agents · Baghdad · operational day 11:00 → 02:30"
        actions={
          <label className="flex items-center gap-3 text-[13px] text-gray-500">
            Day
            <input type="date" value={date} min={addDays(today, -6)} max={today}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="rounded-[9px] border-0 bg-gray-100 px-3 py-1.5 text-[13px] tabular-nums text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" />
          </label>
        }
      />

      <Panel title="All pickup agents" subtitle={day.data ? `${rows.length} agents` : undefined}>
        <div aria-busy={day.isPlaceholderData} className={cn('motion-safe:transition-opacity', day.isPlaceholderData && 'opacity-60')}>
          {day.isLoading ? <Spinner />
            : day.isError && !day.data ? <p className="py-6 text-[13px] text-red-600">Could not load working hours. Try again in a minute.</p>
            : <DayTable rows={rows} selected={driverId} onSelect={pickFromTable} />}
        </div>
      </Panel>

      <div ref={timelineRef} className="scroll-mt-4">
        <Panel
          title="Shift timeline"
          subtitle={selected && <span dir="auto">{selected.name ?? `#${selected.driverId}`}</span>}
          actions={rows.length > 0 && (
            <label className="flex items-center gap-3 text-[13px] text-gray-500">
              Agent
              <Select value={driverId ?? ''} onChange={(e) => setPicked(Number(e.target.value))}
                options={rows.map((r) => ({ value: r.driverId, label: r.name ?? `#${r.driverId}` }))} />
            </label>
          )}
        >
          <div aria-busy={week.isPlaceholderData} className={cn('motion-safe:transition-opacity', week.isPlaceholderData && 'opacity-60')}>
            {driverId === null ? <p className={tbl.empty}>No agents to show for this day.</p>
              : week.isLoading ? <Spinner />
              : week.isError && !week.data ? <p className="py-6 text-[13px] text-red-600">Could not load this agent's week.</p>
              : <ShiftTimeline days={week.data?.days ?? []} />}
          </div>
        </Panel>
      </div>
    </div>
  );
}
